/**
 * Date and time utility functions
 * Helper functions for formatting and parsing date/time values
 */

/**
 * Format a Date as a compact time label, e.g. "4:15pm" (no space, lowercase).
 */
export const formatTimeLabel = (date: Date): string =>
  date
    .toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .replace(/\s/g, '')
    .toLowerCase();

/**
 * Check if a case is within 1 hour based on dueTime
 * @param dueTime - Time until surgery (e.g., "59mins", "2hrs", "Overdue")
 * @returns true if the case is within 1 hour or overdue
 */
export const isWithinOneHour = (dueTime: string): boolean => {
  if (dueTime === 'Overdue') {
    return true;
  }
  // Parse "Xmins" format (e.g., "59mins", "30mins")
  const minsMatch = dueTime.match(/^(\d+)mins$/);
  if (minsMatch) {
    const minutes = parseInt(minsMatch[1], 10);
    return minutes < 60;
  }
  // If it has "hrs", it's >= 1 hour, so return false
  return false;
};

/**
 * Format surgery timestamp
 * Formats the timestamp as "2hrs (8:30am)" or "59mins (8:30am)" or "Overdue (8:30am)"
 * 
 * @param dueTime - Time until surgery (e.g., "2hrs", "59mins", "Overdue")
 * @param surgeryTime - Surgery time in various formats (e.g., "8:30", "10:03 AM", "8:30am")
 * @returns Formatted timestamp string
 */
export const formatSurgeryTimestamp = (dueTime: string, surgeryTime: string): string => {
  // Format surgeryTime to "8:30am" format (no space, lowercase)
  let formattedSurgeryTime = surgeryTime;
  
  // If surgeryTime is in 24-hour format (e.g., "08:30" or "8:30"), convert to 12-hour format
  const time24Match = surgeryTime.match(/^(\d{1,2}):(\d{2})$/);
  if (time24Match) {
    const hours = parseInt(time24Match[1], 10);
    const minutes = time24Match[2];
    const period = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    formattedSurgeryTime = `${displayHours}:${minutes}${period}`;
  } else {
    // If surgeryTime is already in 12-hour format (e.g., "10:03 AM" or "10:03AM"), normalize it
    const time12Match = surgeryTime.match(/^(\d{1,2}):(\d{2})\s*(AM|PM|am|pm)$/i);
    if (time12Match) {
      const hours = time12Match[1];
      const minutes = time12Match[2];
      const period = time12Match[3].toLowerCase();
      formattedSurgeryTime = `${hours}:${minutes}${period}`;
    }
  }
  
  // Return formatted timestamp: "dueTime (formattedSurgeryTime)"
  return `${dueTime} (${formattedSurgeryTime})`;
};

